/**
 * Modal de Onboarding Obrigatório
 * 
 * DESCRIÇÃO:
 * Modal que guia novos usuários através do processo de configuração inicial
 * da conta e da clínica. Aparece automaticamente após o primeiro login e
 * bloqueia o acesso ao sistema até ser completado.
 * 
 * FUNCIONALIDADES:
 * - Steps de configuração: Boas-vindas → Confirmar dados → Finalizar
 * - Validação de dados obrigatórios
 * - Atualização do status de onboarding no banco
 * - Bloqueio de acesso até completar
 * 
 * CONECTA COM:
 * - Tabela user_profiles (campo onboarding_completed)
 * - AuthContext para dados do usuário
 * - ClinicaContext para dados da clínica
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinica } from '@/contexts/ClinicaContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Building2, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}
export const OnboardingModal = ({
  open,
  onComplete
}: OnboardingModalProps) => {
  const {
    user
  } = useAuth();
  const {
    clinicaAtiva,
    recarregarClinica
  } = useClinica();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Dados do formulário de configuração da clínica
  const [clinicData, setClinicData] = useState({
    nome: clinicaAtiva?.nome || '',
    telefone: clinicaAtiva?.telefone || '',
    email: clinicaAtiva?.email || '',
    endereco: clinicaAtiva?.endereco || ''
  });

  // Debug: Log para verificar o estado
  console.log('🔍 [OnboardingModal] Estado atual:', {
    currentStep,
    loading,
    clinicaNome: clinicaAtiva?.nome,
    clinicDataNome: clinicData.nome,
    botaoHabilitado: !(loading || !clinicData.nome.trim()),
    needsOnboarding: open
  });
  const steps = [{
    title: 'Bem-vindo ao seu CRM!',
    description: 'Vamos configurar sua conta em poucos passos.',
    content: 'welcome'
  }, {
    title: 'Confirme os dados da sua clínica',
    description: 'Certifique-se de que as informações estão corretas.',
    content: 'clinic-info'
  }, {
    title: 'Tudo pronto!',
    description: 'Sua conta foi configurada com sucesso.',
    content: 'success'
  }];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setClinicData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Finalizar onboarding e marcar como completo
  const handleComplete = async () => {
    try {
      setLoading(true);

      // Atualizar dados da clínica se necessário
      if (clinicaAtiva?.id) {
        const {
          error: clinicError
        } = await supabase.from('clinicas').update({
          nome: clinicData.nome,
          telefone: clinicData.telefone,
          endereco: clinicData.endereco,
          updated_at: new Date().toISOString()
        }).eq('id', clinicaAtiva.id);
        if (clinicError) throw clinicError;
      }

      // Marcar onboarding como completo
      const {
        error: profileError
      } = await supabase.from('user_profiles').update({
        onboarding_completed: true
      }).eq('user_id', user?.id);
      if (profileError) throw profileError;
      await recarregarClinica();
      toast.success('Configuração concluída com sucesso!');
      onComplete();
    } catch (error) {
      console.error('Erro ao finalizar onboarding:', error);
      toast.error('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  const nextStep = () => {
    console.log('🔄 [OnboardingModal] nextStep chamado:', { currentStep, stepsLength: steps.length });
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };
  const renderStepContent = () => {
    switch (steps[currentStep].content) {
      case 'welcome':
        return <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-lime-100">
              <Sparkles className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold mb-4">
              Olá, {user?.user_metadata?.nome_completo || 'usuário'}!
            </h3>
            <p className="text-gray-600 mb-6">
              Seu pagamento foi confirmado. Vamos configurar sua clínica para você começar a usar as funcionalidades do plano contratado.
            </p>
            <div className="p-4 rounded-lg bg-lime-50">
              <p className="text-sm text-blue-700">
                ✅ Conta criada com sucesso<br />
                ✅ Clínica "{clinicaAtiva?.nome}" configurada<br />
                🔄 Finalizando configurações...
              </p>
            </div>
          </div>;
      case 'clinic-info':
        return <div className="py-4">
            <div className="flex items-center mb-6">
              <Building2 className="w-6 h-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-medium">Dados da Clínica</h3>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="nome" className="text-sm font-medium">
                  Nome da Clínica *
                </Label>
                <Input id="nome" name="nome" value={clinicData.nome} onChange={handleInputChange} placeholder="Nome da sua clínica" className="mt-1" />
              </div>

              <div>
                <Label htmlFor="telefone" className="text-sm font-medium">
                  Telefone
                </Label>
                <Input id="telefone" name="telefone" value={clinicData.telefone} onChange={handleInputChange} placeholder="(11) 99999-9999" className="mt-1" />
              </div>

              <div>
                <Label htmlFor="endereco" className="text-sm font-medium">
                  Endereço
                </Label>
                <Input id="endereco" name="endereco" value={clinicData.endereco} onChange={handleInputChange} placeholder="Endereço da clínica" className="mt-1" />
              </div>
            </div>
          </div>;
      case 'success':
        return <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold mb-4">
              Configuração Concluída!
            </h3>
            <p className="text-gray-600 mb-6">
              Sua conta está pronta para usar. Explore todas as funcionalidades do CRM e comece a gerenciar seus leads e pacientes.
            </p>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">
                🎉 Assinatura ativa<br />
                📊 Dashboard configurado<br />
                💬 Chat pronto para receber leads<br />
                📋 Pipeline de vendas criado
              </p>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-center">
            {steps[currentStep].title}
          </DialogTitle>
          <DialogDescription className="text-center">
            {steps[currentStep].description}
          </DialogDescription>
        </DialogHeader>

        {/* Indicador de progresso */}
        <div className="flex items-center justify-center mb-4">
          {steps.map((_, index) => <React.Fragment key={index}>
              <div className={`w-3 h-3 rounded-full ${index <= currentStep ? 'bg-blue-600' : 'bg-gray-300'}`} />
              {index < steps.length - 1 && <div className={`w-8 h-px ${index < currentStep ? 'bg-blue-600' : 'bg-gray-300'}`} />}
            </React.Fragment>)}
        </div>

        <Separator className="my-4" />

        {/* Conteúdo do step atual */}
        {renderStepContent()}

        <Separator className="my-4" />

        {/* Botões de navegação */}
        <div className="flex justify-end">
          <Button 
            onClick={nextStep} 
            disabled={loading || (currentStep === 1 && !clinicData.nome.trim())} 
            className="min-w-[120px] rounded-xl bg-lime-500 hover:bg-lime-400 text-slate-950"
          >
            {loading ? 'Salvando...' : currentStep === steps.length - 1 ? 'Finalizar' : <>
                Próximo
                <ArrowRight className="ml-2 w-4 h-4" />
              </>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>;
};
